import { useState } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { DailyPoint } from "../lib/aggregate";
import { formatUsd } from "../lib/format";

interface Props {
  daily: DailyPoint[];
  range: "7d" | "30d" | "90d" | "all";
  selectedDate?: string | null;
  onSelectDate?: (date: string | null) => void;
}

function buildBuckets(daily: DailyPoint[], days: number): DailyPoint[] {
  const map = new Map(daily.map((d) => [d.date, d]));
  const buckets: DailyPoint[] = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    const key = `${y}-${m}-${day}`;
    const existing = map.get(key);
    if (existing) buckets.push(existing);
    else
      buckets.push({
        date: key,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        audioSeconds: 0,
        costUsd: 0,
        callCount: 0,
      });
  }
  return buckets;
}

function formatDateLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function UsageTimeline({ daily, range, selectedDate, onSelectDate }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const days = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : Math.max(daily.length, 1);
  const points = range === "all" ? daily : buildBuckets(daily, days);
  const maxCost = Math.max(...points.map((p) => p.costUsd), 0.0001);
  const totalCost = points.reduce((s, p) => s + p.costUsd, 0);
  const active = hover !== null ? points[hover] : null;
  const handleClick = (date: string) => {
    if (!onSelectDate) return;
    onSelectDate(selectedDate === date ? null : date);
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground tracking-tight">
            Custo por dia
          </h3>
          <p className="text-xs text-muted-foreground">
            {points.length} {points.length === 1 ? "dia" : "dias"} · {formatUsd(totalCost)}
          </p>
        </div>
        {active ? (
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {formatDateLabel(active.date)}
            </p>
            <p className="text-sm font-semibold text-foreground tabular-nums">
              {formatUsd(active.costUsd)}
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {active.callCount} {active.callCount === 1 ? "call" : "calls"}
              </span>
            </p>
          </div>
        ) : null}
      </div>

      {points.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Sem dados no período
        </p>
      ) : (
        <div
          className="relative flex items-end gap-[2px] h-32"
          onMouseLeave={() => setHover(null)}
        >
          {points.map((p, idx) => {
            const h = (p.costUsd / maxCost) * 100;
            const isHover = hover === idx;
            const isSelected = selectedDate === p.date;
            const hasCost = p.costUsd > 0;
            return (
              <button
                type="button"
                key={`${p.date}-${idx}`}
                onMouseEnter={() => setHover(idx)}
                onFocus={() => setHover(idx)}
                onClick={() => handleClick(p.date)}
                aria-label={`${formatDateLabel(p.date)}: ${formatUsd(p.costUsd)}`}
                aria-pressed={isSelected}
                className={cn(
                  "group relative flex-1 h-full flex flex-col items-stretch justify-end rounded-sm transition-colors outline-none cursor-pointer",
                  isSelected
                    ? "bg-primary/20 ring-1 ring-primary/40"
                    : isHover
                    ? "bg-primary/10"
                    : "hover:bg-primary/5",
                )}
              >
                <div
                  className={cn(
                    "w-full rounded-sm transition-colors",
                    hasCost
                      ? isSelected || isHover
                        ? "bg-primary"
                        : "bg-primary/70"
                      : "bg-muted-foreground/20",
                  )}
                  style={{
                    height: hasCost ? `${Math.max(h, 6)}%` : "2px",
                  }}
                />
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
